import {
  Card,
  CardContent,
  Grid,
  TextField,
  Typography,
  Button,
  FormControlLabel,
  Checkbox,
  IconButton,
  Tooltip,
  Chip,
  Avatar,
  Box,
} from "@mui/material";
import config from "../../utils/config";
import { toast } from "react-toastify";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { Editor } from "react-draft-wysiwyg";
import { EditorState } from "draft-js";
import { stateToHTML } from "draft-js-export-html";
import { stateFromHTML } from "draft-js-import-html";
import parse from "html-react-parser";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddOption from "./AddOption";
import ClearIcon from "@mui/icons-material/Clear";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import moment from "moment";
import { useState, useEffect } from "react";
import axios from "axios";
import { useDebouncedCallback } from "use-debounce";
import { useNavigate, useParams } from "react-router-dom";

const stateToHtmlOptions = {
  entityStyleFn: (entity) => {
    if (entity.get("type").toLowerCase() === "link") {
      const data = entity.getData();
      return {
        element: "a",
        attributes: {
          href: data.url,
          target: "_blank",
        },
      };
    }
  },
};

function AddEvent(props) {
  const navigate = useNavigate();

  const { editEventId, cloneEventId } = useParams();

  const [organizationList, setOrganizationList] = useState([]);
  const [name, setName] = useState("");
  const [datetime, setDatetime] = useState(null);
  const [datetimeEnd, setDatetimeEnd] = useState(null);
  const [datetimeLimit, setDatetimeLimit] = useState(null);
  const [briefDescription, setBriefDescription] = useState("");
  const [location, setLocation] = useState("");
  const [regularPrice, setRegularPrice] = useState(0.0);
  const [esnCardPrice, setEsnCardPrice] = useState(null);
  const [esnCardRequired, setEsnCardRequired] = useState(false);
  const [esnCardBuyPrice, setEsnCardBuyPrice] = useState(null);
  const [file, setFile] = useState(null);
  const [initialPicture, setInitialPicture] = useState(null);
  const [imageInputValue, setImageInputValue] = useState("");
  const [seats, setSeats] = useState(null);
  const [sharedSeats, setSharedSeats] = useState({});
  const [useRegularSeats, setUseRegularSeats] = useState(false);
  const [options, setOptions] = useState([]);
  const [externalPayLink, setExternalPayLink] = useState("");
  const [externalPayLabel, setExternalPayLabel] = useState("");
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [editorState2, setEditorState2] = useState(EditorState.createEmpty());
  const [editorState3, setEditorState3] = useState(EditorState.createEmpty());
  const [addingOption, setAddingOption] = useState(false);
  const [editOptionIndex, setEditOptionIndex] = useState(null);
  const [active, setActive] = useState(true);
  const [shared, setShared] = useState(false);
  const [hostInvoicing, setHostInvoicing] = useState(false);
  const [wishlistEnabled, setWishlistEnabled] = useState(false);
  const [allowExternalCards, setAllowExternalCards] = useState(false);
  const [onlineOnly, setOnlineOnly] = useState(false);
  const [changes, setChanges] = useState([]);

  useEffect(() => {
    if (editEventId) {
      props.setTitle("Edit Event");
    } else if (cloneEventId) {
      props.setTitle("Clone Event");
    } else {
      props.setTitle("Create Event");
    }
  }, [editEventId, cloneEventId]); //eslint-disable-line

  useEffect(() => {
    getOrganizationList();
    if (editEventId) {
      fetchEventInfo();
      fetchEventChanges();
    } else if (cloneEventId) {
      fetchEventInfo();
    } else {
      resetState();
    }
  }, [props.organization, editEventId, cloneEventId]); //eslint-disable-line

  const resetState = () => {
    setName("");
    setDatetime(null);
    setDatetimeEnd(null);
    setDatetimeLimit(null);
    setBriefDescription("");
    setLocation("");
    setRegularPrice(0.0);
    setEsnCardPrice(null);
    setEsnCardRequired(false);
    setEsnCardBuyPrice(null);
    setFile(null);
    setInitialPicture(null);
    setSeats(null);
    setSharedSeats({});
    setUseRegularSeats(false);
    setOptions([]);
    setExternalPayLink("");
    setExternalPayLabel("");
    setEditorState(EditorState.createEmpty());
    setEditorState2(EditorState.createEmpty());
    setEditorState3(EditorState.createEmpty());
    setAddingOption(false);
    setEditOptionIndex(null);
    setActive(true);
    setShared(false);
    setHostInvoicing(false);
    setWishlistEnabled(false);
    setAllowExternalCards(false);
    setOnlineOnly(false);
  };

  const setStateFromEvent = (event) => {
    setName(event.name);
    setDatetime(moment(event.datetime));
    setDatetimeEnd(event.datetimeEnd ? moment(event.datetimeEnd) : null);
    setDatetimeLimit(event.datetimeLimit ? moment(event.datetimeLimit) : null);
    setBriefDescription(event.briefDescription);
    setLocation(event.location);
    setRegularPrice(event.regularPrice);
    setEsnCardPrice(event.esnCardPrice);
    setEsnCardRequired(event.esnCardRequired);
    setEsnCardBuyPrice(event.esnCardBuyPrice);
    setInitialPicture(
      event.imageURL && editEventId ? `${config.IMG_ENDPOINT}/uploads/${event.imageURL}` : null
    );
    setSeats(event.seats);
    setSharedSeats(event.sharedSeats ?? {});
    setUseRegularSeats(event.useRegularSeats ?? false);
    setOptions(event.options ?? []);
    setEditorState(
      event.description !== ""
        ? EditorState.createWithContent(stateFromHTML(event.description))
        : EditorState.createEmpty()
    );
    setEditorState2(
      event.customEmailInfo !== ""
        ? EditorState.createWithContent(stateFromHTML(event.customEmailInfo))
        : EditorState.createEmpty()
    );
    setEditorState3(
      event.customPurchaseEmailInfo !== ""
        ? EditorState.createWithContent(stateFromHTML(event.customPurchaseEmailInfo))
        : EditorState.createEmpty()
    );
    setActive(event.active);
    setShared(event.shared);
    setHostInvoicing(event.hostInvoicing);
    setWishlistEnabled(event.wishlistEnabled);
    setAllowExternalCards(event.allowExternalCards);
    setExternalPayLink(event.externalPayLink ?? "");
    setExternalPayLabel(event.externalPayLabel ?? "");
    setOnlineOnly(event.onlineOnly ?? false);
  };

  const handleClearPhoto = () => {
    setFile(null);
    setInitialPicture(null);
    setImageInputValue("");
  };

  const getOrganizationList = () => {
    axios
      .get(`${config.API_ENDPOINT}/organizations/all`, {
        headers: { "x-access-token": localStorage.getItem("access-token") },
      })
      .then((res) => {
        let list = [];
        for (let org of res.data) {
          list.push({
            name: org.name,
            id: org._id,
          });
        }

        setOrganizationList(list);
      })
      .catch((err) => {
        switch (err.response?.status) {
          case 401:
            localStorage.removeItem("access-token");
            window.location.replace("/login");
            break;
          default:
            toast.error("An unexpected error has occurred");
            break;
        }
      });
  };

  const fetchEventInfo = () => {
    var id = editEventId ?? cloneEventId;

    axios
      .get(`${config.API_ENDPOINT}/events/${props.organization}/${id}`)
      .then((res) => setStateFromEvent(res.data))
      .catch((err) => {
        switch (err.response?.status) {
          case 401:
            localStorage.removeItem("access-token");
            window.location.replace("/login");
            break;
          default:
            toast.error("An unexpected error has occurred");
            break;
        }
      });
  };

  const fetchEventChanges = () => {
    const id = editEventId;

    axios
      .get(`${config.API_ENDPOINT}/events/${props.organization}/${id}/changes`, {
        headers: { "x-access-token": localStorage.getItem("access-token") },
      })
      .then((res) => {
        setChanges(res.data);
      })
      .catch((err) => {
        switch (err.response?.status) {
          case 401:
            localStorage.removeItem("access-token");
            window.location.replace("/login");
            break;
          default:
            toast.error("An unexpected error has occurred");
            break;
        }
      });
  };

  const submit = useDebouncedCallback(
    () => {
      if (name === "" || datetime == null || location === "") {
        toast.warning("You must fill the required fields.");
        return;
      }

      if (
        (seats != null && seats < 0) ||
        (regularPrice != null && regularPrice < 0) ||
        (esnCardPrice != null && esnCardPrice < 0) ||
        (esnCardBuyPrice != null && esnCardBuyPrice < 0) ||
        (sharedSeats != null && Object.values(sharedSeats).some((val) => val < 0))
      ) {
        toast.warning("No value can be less than 0.");
        return;
      }

      const data = new FormData();
      data.append("name", name);
      data.append("datetime", datetime.valueOf());
      data.append("briefDescription", briefDescription);
      data.append("description", stateToHTML(editorState.getCurrentContent(), stateToHtmlOptions));
      data.append("location", location);
      data.append("regularPrice", regularPrice ?? 0.0);
      data.append("esnCardRequired", esnCardRequired);
      data.append("organization", props.organization);
      data.append("createdBy", props.user.name);
      data.append("options", JSON.stringify(options));
      data.append(
        "customEmailInfo",
        stateToHTML(editorState2.getCurrentContent(), stateToHtmlOptions)
      );
      data.append(
        "customPurchaseEmailInfo",
        stateToHTML(editorState3.getCurrentContent(), stateToHtmlOptions)
      );
      data.append("sharedSeats", JSON.stringify(sharedSeats));
      data.append("useRegularSeats", useRegularSeats);
      data.append("active", active);
      data.append("shared", shared);

      if (shared) {
        data.append("hostInvoicing", hostInvoicing);
      }

      data.append("wishlistEnabled", wishlistEnabled);
      data.append("allowExternalCards", allowExternalCards);
      data.append("onlineOnly", onlineOnly);

      if (esnCardPrice != null || editEventId != null) {
        data.append("esnCardPrice", esnCardPrice);
      }

      if (datetimeEnd != null || editEventId != null) {
        data.append("datetimeEnd", datetimeEnd != null ? datetimeEnd.valueOf() : null);
      }

      if (seats != null || editEventId != null) {
        data.append("seats", seats);
      }

      if (datetimeLimit != null || editEventId != null) {
        data.append("datetimeLimit", datetimeLimit != null ? datetimeLimit.valueOf() : null);
      }

      if (esnCardBuyPrice != null || editEventId != null) {
        data.append("esnCardBuyPrice", esnCardBuyPrice);
      }

      if (externalPayLink !== "") {
        data.append("externalPayLink", externalPayLink);
      } else if (editEventId !== null) {
        data.append("externalPayLink", null);
      }

      if (externalPayLabel !== "") {
        data.append("externalPayLabel", externalPayLabel);
      } else if (editEventId !== null) {
        data.append("externalPayLabel", null);
      }

      if (file != null) {
        data.append("image", file);
      }

      let q = `${config.API_ENDPOINT}/events/create`;
      if (editEventId != null) {
        q = `${config.API_ENDPOINT}/events/${props.organization}/${editEventId}`;

        if (initialPicture == null && file == null) {
          data.append("deleteImage", true);
        }
      }

      axios
        .post(q, data, {
          headers: {
            "Content-Type": "multipart/form-data",
            "x-access-token": localStorage.getItem("access-token"),
          },
        })
        .then((res) => {
          let created = res.data;

          toast.success("Success!");

          if (editEventId == null) {
            navigate(`/edit-event/${created._id}`);
          } else {
            fetchEventChanges();
          }
        })
        .catch((err) => {
          switch (err.response?.status) {
            case 401:
              localStorage.removeItem("access-token");
              window.location.replace("/login");
              break;
            case 413:
              toast.warning("The image you sent is too big.");
              break;
            default:
              toast.error("An unexpected error has occurred");
              break;
          }
        });
    },
    500,
    { leading: true }
  );

  const copyIframe = async () => {
    let text = `<iframe frameborder="0" scrolling="yes" src="${config.WEB_ENDPOINT}/iframes/event-details/${props.organization}/${editEventId}" style=" min-height: 700px; overflow-y: scroll; width: 100%; "></iframe>`;
    await navigator.clipboard.writeText(text);
    toast.success("Copied!");
  };

  const getChangesUI = () => {
    return (
      <div>
        <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
          Change history
        </Typography>
        <Box sx={{ maxHeight: 600, overflowY: "auto" }}>
          {changes.map((change, index) => (
            <Card key={index} sx={{ mb: 1 }}>
              <CardContent>
                <Box display="flex" flexDirection="row" alignItems="center">
                  <Avatar
                    alt=""
                    src={
                      change.user?.avatarURL != null
                        ? `${config.IMG_ENDPOINT}/uploads/${change.user.avatarURL}`
                        : null
                    }
                  >
                    {change.user?.name.charAt(0) ?? "D"}
                  </Avatar>
                  <Box ml={2}>
                    <Typography color="text.secondary">
                      {change.user?.name ?? "Deleted User"}
                    </Typography>
                    <Typography color="text.secondary" fontSize={11}>
                      {moment(change.datetime).format("DD/MM/YYYY HH:mm")}
                    </Typography>
                    <Box display="flex" flexDirection="row" alignItems="center" flexWrap="wrap">
                      {Object.entries(change.changes).map(([key, _]) => (
                        <Chip label={key} sx={{ mr: 1, mt: 1 }} />
                      ))}
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      </div>
    );
  };

  const getSeatsInputUI = () => {
    if (shared && !useRegularSeats) {
      return (
        <Box>
          {organizationList.map((org) => (
            <Grid container alignItems="center" sx={{ mb: 2 }} key={org.id}>
              <Grid item xs={6} sx={{ textAlign: "center" }} key={org.name}>
                <Typography>{org.name}</Typography>
              </Grid>
              <Grid item xs={6} key={org.id}>
                <TextField
                  variant="outlined"
                  fullWidth
                  label="Seats"
                  name="seats"
                  type="number"
                  value={sharedSeats[org.id] ?? ""}
                  onChange={(e) => {
                    let ns = { ...sharedSeats };
                    ns[org.id] = e.target.value !== "" ? parseInt(e.target.value) : null;

                    setSharedSeats(ns);
                  }}
                  sx={{ mb: 1 }}
                />
              </Grid>
            </Grid>
          ))}
        </Box>
      );
    } else {
      return (
        <TextField
          variant="outlined"
          fullWidth
          label="Seats"
          name="seats"
          type="number"
          value={seats ?? ""}
          onChange={(e) => setSeats(e.target.value !== "" ? parseInt(e.target.value) : null)}
          sx={{ mb: 2 }}
        />
      );
    }
  };

  const previewUI = () => {
    return (
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              Preview
            </Typography>
            {editEventId ? (
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mb: 2 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={copyIframe}
                  sx={{ textTransform: "none", fontWeight: 600 }}
                >
                  Copy Embed Code
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ textTransform: "none", fontWeight: 600 }}
                  onClick={() => {
                    window.open(
                      `${config.WEB_ENDPOINT}/iframes/event-details/${props.organization}/${editEventId}/internal`,
                      "_blank"
                    );
                  }}
                >
                  Open Internal Sale
                </Button>
              </Box>
            ) : null}
            {file || initialPicture ? (
              <img
                alt="Event"
                height={150}
                width={"100%"}
                style={{ objectFit: "contain" }}
                src={file ? URL.createObjectURL(file) : initialPicture}
              />
            ) : (
              <Box
                sx={{
                  height: 150,
                  width: "100%",
                  backgroundColor: "rgba(19, 33, 68, 0.05)",
                  borderRadius: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 12,
                  color: "text.secondary",
                }}
              >
                No image
              </Box>
            )}
            <Typography sx={{ mt: 3, textAlign: "center", fontSize: 28, fontWeight: 700 }}>{name}</Typography>
            <Typography sx={{ mt: 2 }}>
              <strong>{datetimeEnd ? "From: " : "Date: "}</strong>
              {datetime ? datetime.format("dddd, DD/MM/YYYY HH:mm") : ""}
            </Typography>
            {datetimeEnd ? (
              <Typography sx={{ mt: 1.5 }}>
                <strong>To: </strong>
                {datetimeEnd.format("dddd, DD/MM/YYYY HH:mm")}
              </Typography>
            ) : null}
            <Typography sx={{ mt: 1.5 }}>
              <strong>Location: </strong>
              {location}
            </Typography>
            <Box sx={{ mt: 2 }}>{parse(stateToHTML(editorState.getCurrentContent(), stateToHtmlOptions))}</Box>
            <Typography sx={{ mt: 2, textAlign: "end", fontSize: 28, fontWeight: 700 }}>
              {(regularPrice ?? 0.0).toFixed(2) + "€"}
            </Typography>
            <Typography sx={{ mt: 0.5, textAlign: "end" }}>
              {!esnCardRequired && esnCardPrice != null
                ? esnCardPrice.toFixed(2) + "€ with ESNcard"
                : "Only for students with ESNcard"}
            </Typography>

            <Card sx={{ mt: 2, mb: 1 }}>
              <CardContent>
                <Typography sx={{ fontWeight: 600, mb: 1 }}>Custom Ticket Email Section</Typography>
                {parse(stateToHTML(editorState2.getCurrentContent(), stateToHtmlOptions))}
              </CardContent>
            </Card>
            <Card>
              <CardContent>
                <Typography sx={{ fontWeight: 600, mb: 1 }}>Custom Purchase Request Email Section</Typography>
                {parse(stateToHTML(editorState3.getCurrentContent(), stateToHtmlOptions))}
              </CardContent>
            </Card>

            <Box sx={{ mt: 2, display: "flex", flexDirection: "column" }}>
              <FormControlLabel
                sx={{ mt: 1 }}
                value="start"
                control={<Checkbox color="primary" checked={active} onChange={(e) => setActive(e.target.checked)} />}
                label="Active?"
                labelPlacement="start"
              />
              <Tooltip title="If enabled, students from other organizations may use their ESN cards on this event.">
                <FormControlLabel
                  sx={{ mt: 1 }}
                  value="start"
                  control={
                    <Checkbox
                      color="primary"
                      checked={allowExternalCards}
                      onChange={(e) => setAllowExternalCards(e.target.checked)}
                    />
                  }
                  label="Allow external cards?"
                  labelPlacement="start"
                />
              </Tooltip>
              <FormControlLabel
                sx={{ mt: 1 }}
                value="start"
                control={<Checkbox color="primary" checked={shared} onChange={(e) => setShared(e.target.checked)} />}
                label="Shared Event?"
                labelPlacement="start"
              />
              {shared ? (
                <Tooltip title="If enabled, invoicing will be emitted from the host organization, rather than the individual joiners.">
                  <FormControlLabel
                    sx={{ mt: 1 }}
                    value="start"
                    control={
                      <Checkbox
                        color="primary"
                        checked={hostInvoicing}
                        onChange={(e) => setHostInvoicing(e.target.checked)}
                      />
                    }
                    label="Host Invoicing?"
                    labelPlacement="start"
                  />
                </Tooltip>
              ) : null}
              <FormControlLabel
                sx={{ mt: 1 }}
                value="start"
                control={
                  <Checkbox
                    color="primary"
                    checked={wishlistEnabled}
                    onChange={(e) => setWishlistEnabled(e.target.checked)}
                  />
                }
                label="Wishlist Enabled?"
                labelPlacement="start"
              />
              <FormControlLabel
                sx={{ mt: 1 }}
                value="start"
                control={<Checkbox color="primary" checked={onlineOnly} onChange={(e) => setOnlineOnly(e.target.checked)} />}
                label="Online Payments Only?"
                labelPlacement="start"
              />
            </Box>

            <Button sx={{ mt: 3, color: "white" }} variant="contained" color="primary" onClick={submit}>
              {editEventId ? "Update Event" : "Create Event"}
            </Button>
            {changes.length > 0 && getChangesUI()}
          </CardContent>
        </Card>
      </Grid>
    );
  };

  const addFormUI = () => {
    return (
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              Event Details
            </Typography>
        <TextField
          variant="outlined"
          required
          fullWidth
          autoFocus
          label="Name"
          name="name"
          type="text"
          autoComplete="off"
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ mb: 2 }}
        />
        <TextField
          variant="outlined"
          fullWidth
          label="Brief Description"
          name="description"
          type="text"
          autoComplete="off"
          value={briefDescription}
          onChange={(e) => setBriefDescription(e.target.value)}
          sx={{ mb: 2 }}
        />
        <input
          accept="image/jpg, image/jpeg"
          id="icon-button-file"
          type="file"
          hidden
          value={imageInputValue}
          onChange={(e) => {
            setFile(e.target.files[0]);
            setImageInputValue(e.target.value);
          }}
        />
        <label htmlFor="icon-button-file">
          Event Picture
          <IconButton color="primary" aria-label="upload picture" component="span">
            <PhotoCamera />
          </IconButton>
        </label>

        {file || initialPicture ? (
          <IconButton onClick={handleClearPhoto}>
            <ClearIcon />
          </IconButton>
        ) : null}
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Editor
              editorStyle={{ minHeight: 200 }}
              editorState={editorState}
              label="Description..."
              onEditorStateChange={(s) => setEditorState(s)}
            />
          </CardContent>
        </Card>
        <DateTimePicker
          label="Date and Time of the Event *"
          inputVariant="outlined"
          required
          format="DD/MM/YYYY HH:mm"
          value={datetime}
          ampm={false}
          onChange={(date) => setDatetime(date)}
          slotProps={{ textField: {sx: { mb: 2 }, fullWidth: true}}}
        />
        <DateTimePicker
          label="End Date and Time of the Event"
          inputVariant="outlined"
          fullWidth
          format="DD/MM/YYYY HH:mm"
          value={datetimeEnd}
          ampm={false}
          onChange={(date) => setDatetimeEnd(date)}
          slotProps={{ textField: {sx: { mb: 2 }, fullWidth: true}}}
        />
        <DateTimePicker
          label="Limit Date and Time for Purchase"
          inputVariant="outlined"
          fullWidth
          format="DD/MM/YYYY HH:mm"
          value={datetimeLimit}
          ampm={false}
          onChange={(date) => setDatetimeLimit(date)}
          slotProps={{ textField: {sx: { mb: 2 }, fullWidth: true}}}
        />
        <TextField
          variant="outlined"
          fullWidth
          multiline
          label="Location"
          name="location"
          type="text"
          autoComplete="off"
          value={location}
          required
          onChange={(e) => setLocation(e.target.value)}
          sx={{ mb: 2 }}
        />
        {getSeatsInputUI()}
        {shared && (
          <FormControlLabel
            sx={{ mb: 2 }}
            value="start"
            control={
              <Checkbox
                color="primary"
                checked={useRegularSeats}
                onChange={(e) => {
                  setSharedSeats({});
                  setUseRegularSeats(e.target.checked);
                }}
              />
            }
            label="Use total seats"
            labelPlacement="start"
          />
        )}
        <TextField
          variant="outlined"
          fullWidth
          label={!esnCardRequired ? "Regular Price" : "Price"}
          name="regularPrice"
          type="number"
          value={regularPrice ?? ""}
          required
          onChange={(e) =>
            setRegularPrice(e.target.value !== "" ? parseFloat(e.target.value) : null)
          }
          inputProps={{ step: "0.01" }}
          sx={esnCardRequired ? { mb: 2 } : {}}
        />
        {!esnCardRequired ? (
          <Typography sx={{ mb: 2, fontSize: 10 }}>
            <strong>The regular price must include VAT</strong>
          </Typography>
        ) : null}
        {!esnCardRequired ? (
          <TextField
            variant="outlined"
            fullWidth
            label="Price with ESNcard"
            name="esnCardPrice"
            type="number"
            value={esnCardPrice ?? ""}
            required
            onChange={(e) =>
              setEsnCardPrice(e.target.value !== "" ? parseFloat(e.target.value) : null)
            }
            inputProps={{ step: "0.01" }}
            sx={{ mb: 2 }}
          />
        ) : null}
        <TextField
          variant="outlined"
          fullWidth
          label="Special Price (24h)"
          name="discount"
          type="number"
          value={esnCardBuyPrice ?? ""}
          onChange={(e) =>
            setEsnCardBuyPrice(e.target.value !== "" ? parseFloat(e.target.value) : null)
          }
          inputProps={{ step: "0.01" }}
          sx={{ mb: 2 }}
        />
        <FormControlLabel
          sx={{ mb: 2 }}
          value="start"
          control={
            <Checkbox
              color="primary"
              checked={esnCardRequired}
              onChange={(e) => {
                setEsnCardPrice(null);
                setEsnCardRequired(e.target.checked);
              }}
            />
          }
          label="ESNcard required?"
          labelPlacement="start"
        />
        <br />
        <Typography sx={{ display: "inline", mr: 0.5 }}>Options</Typography>
        <IconButton onClick={() => setAddingOption(true)}>
          <AddIcon />
        </IconButton>
        <br />
        {options.map((option, index) => (
          <Box key={index}>
            <Typography sx={{ display: "inline", mr: 0.5 }}>{option.name}</Typography>
            <IconButton onClick={() => setEditOptionIndex(index)}>
              <EditIcon />
            </IconButton>
            <IconButton
              onClick={() => {
                let opts = [...options];
                opts.splice(index, 1);
                setOptions(opts);
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        ))}

        <TextField
          variant="outlined"
          fullWidth
          multiline
          label="External Payment Link"
          name="externalPayLink"
          type="text"
          autoComplete="off"
          value={externalPayLink}
          onChange={(e) => setExternalPayLink(e.target.value)}
          sx={{ mt: 2 }}
        />
        <TextField
          variant="outlined"
          fullWidth
          multiline
          label="External Payment Label (e.g Pay with Verse)"
          name="externalPayLabel"
          type="text"
          autoComplete="off"
          value={externalPayLabel}
          onChange={(e) => setExternalPayLabel(e.target.value)}
          sx={{ mt: 2 }}
        />

        <Typography color="gray" sx={{ mt: 2 }}>
          Custom Ticket Email Section
        </Typography>
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Editor
              editorStyle={{ minHeight: 200 }}
              editorState={editorState2}
              onEditorStateChange={(s) => setEditorState2(s)}
            />
          </CardContent>
        </Card>
        <Typography color="gray">Custom Purchase Request Email Section</Typography>
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Editor
              editorStyle={{ minHeight: 200 }}
              editorState={editorState3}
              onEditorStateChange={(s) => setEditorState3(s)}
            />
          </CardContent>
        </Card>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  if (props.user.accessLevel > 3) return null;

  return (
    <div>
      <Box sx={{ mb: 4, pb: 3, borderBottom: "1px solid rgba(19, 33, 68, 0.1)" }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
          {editEventId ? "Edit Event" : cloneEventId ? "Clone Event" : "Create Event"}
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Configure event details, pricing, options, and communication content
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {addFormUI()}
        {previewUI()}
      </Grid>

      <AddOption
        open={addingOption}
        onClose={() => setAddingOption(false)}
        onConfirm={(val) => {
          setOptions([...options, val]);
          setAddingOption(false);
        }}
      />

      <AddOption
        open={editOptionIndex != null}
        onClose={() => setEditOptionIndex(null)}
        editOption={editOptionIndex != null ? options[editOptionIndex] : null}
        onConfirm={(val) => {
          let newOptions = [...options];
          newOptions[editOptionIndex] = val;
          setOptions(newOptions);
          setEditOptionIndex(null);
        }}
      />
    </div>
  );
}

export default AddEvent;
